import type { IncomeStatementLine, IncomeStatementMonth, IncomeStatementResponse } from '../incomeStatement/incomeStatementApi';
import type { BudgetPlan, CompanyBudgetWorkspaceDto } from '../companyBudget/companyBudgetApi';
import { UNGROUPED_COST_GROUP, type CompanyCostGroup } from '../../companyCosts/costGroups';
import { addLineDeltas, addPlanPending, emptyPendingScope, isScopePending } from '../pendingScope';

// Pure view model of the "Pulpit zarządu" (management dashboard). Money is
// computed by the backend report services (income statement, company budget,
// liquidity); this file only folds it for display: cumulative series, period
// slices, targets and the forecast scenarios. Never recompute recognition,
// costs or margins here.

// The tabs of „Wynik firmy” (Profitability.tsx `TAB_KEYS`); the period is
// `reportPeriod.ts` (one for every tab, PR2 2026-10-29).
export const DASHBOARD_VIEWS = ['overview', 'orders', 'costs', 'cash', 'details'] as const;
export type DashboardView = typeof DASHBOARD_VIEWS[number];

export type DashboardScenario = 'low' | 'base' | 'high';
export const DASHBOARD_SCENARIOS: DashboardScenario[] = ['base', 'low', 'high'];

// Scenarios move only the still-planned part of the forecast (orders not yet
// recognized); realized amounts are facts and stay unchanged.
export const SCENARIO_FACTORS: Record<DashboardScenario, number> = { low: 0.88, base: 1, high: 1.12 };

/**
 * A sum over months where some months hold an incomplete cost: since
 * 2026-10-29 the backend sends the known part with `pendingCostOrderCount`
 * (`pending` here); a `null` month (kept for safety) is left out. The known
 * part is summed, `incomplete` marks it. `value` is null only when every month
 * is unknown.
 */
export interface PartialSum {
  value: number | null;
  incomplete: boolean;
}

export function partialSum(values: (number | null)[], pending: boolean[] = []): PartialSum {
  let total = 0;
  let known = 0;
  values.forEach((value) => {
    if (value == null) return;
    total += value;
    known += 1;
  });
  return {
    value: known === 0 && values.length > 0 ? null : total,
    incomplete: known < values.length || pending.some(Boolean),
  };
}

/** The line holds ZK counted only with their known cost. */
export function linePending(line: IncomeStatementLine): boolean {
  return (line.pendingCostOrderCount ?? 0) > 0;
}

/**
 * The months summed hold a ZK whose cost is still incomplete after the sum
 * (`pendingScope.ts`, one „*” rule for every range): ledger changes are added
 * up over the months, a plan part without an average marks any range. Lines
 * without deltas keep their own flags.
 */
export function monthsPending(
  months: { month: string; actual: IncomeStatementLine; forecast: IncomeStatementLine }[],
  basis: 'actual' | 'forecast',
): boolean {
  const scope = emptyPendingScope();
  months.forEach((month) => {
    const ledgerDeltas = month.actual.pendingCostDeltas;
    if (ledgerDeltas === undefined && basis === 'forecast') {
      if (linePending(month.forecast)) addPlanPending(scope, `${month.month}:forecast`);
      return;
    }
    addLineDeltas(scope, ledgerDeltas, month.actual.pendingCostOrderCount ?? 0, `${month.month}:actual`);
    if (basis === 'forecast' && (month.forecast.planPendingCostOrderCount ?? 0) > 0) addPlanPending(scope, `${month.month}:plan`);
  });
  return isScopePending(scope);
}

export interface DashboardMonth {
  month: string;
  index: number;
  /** Elapsed or current month (realization exists). */
  elapsed: boolean;
  isCurrent: boolean;
  revenueTarget: number | null;
  profitTarget: number | null;
  /** The month's plan (budget), null without a saved plan (decision 2026-10-29-grupy-kosztow-i-budzet). */
  plan: BudgetPlan | null;
  actualRevenue: number;
  actualProfit: number | null;
  /** Realization + planned part moved by the scenario factor. */
  forecastRevenue: number;
  forecastProfit: number | null;
  /** Margin of the forecast moved by the scenario like revenue and profit. */
  forecastMargin: number | null;
  /** Realization / forecast hold an incomplete cost (known part, marked). */
  actualCostPending: boolean;
  forecastCostPending: boolean;
  actual: IncomeStatementLine;
  forecast: IncomeStatementLine;
}

function scaled(actual: number | null, forecast: number | null, factor: number): number | null {
  if (actual == null || forecast == null) return forecast == null ? null : forecast;
  return actual + (forecast - actual) * factor;
}

export function buildDashboardMonths(
  statement: IncomeStatementResponse,
  budget: CompanyBudgetWorkspaceDto | null,
  scenario: DashboardScenario,
): DashboardMonth[] {
  const factor = SCENARIO_FACTORS[scenario];
  const targets = new Map((budget?.months ?? []).map((month) => [month.month, month]));
  return statement.months.map((month: IncomeStatementMonth, index) => {
    const target = targets.get(month.month);
    const revenueTarget = target && target.budget.revenue > 0 ? target.budget.revenue : null;
    return {
      month: month.month,
      index,
      elapsed: !month.isFuture,
      isCurrent: month.isCurrent,
      revenueTarget,
      profitTarget: target?.netProfitTarget ?? null,
      plan: target?.budget.hasPlan ? target.budget : null,
      actualRevenue: month.actual.revenue,
      actualProfit: month.actual.profit,
      forecastRevenue: scaled(month.actual.revenue, month.forecast.revenue, factor) ?? 0,
      forecastProfit: scaled(month.actual.profit, month.forecast.profit, factor),
      forecastMargin: scaled(month.actual.margin, month.forecast.margin, factor),
      actualCostPending: linePending(month.actual),
      forecastCostPending: linePending(month.forecast),
      actual: month.actual,
      forecast: month.forecast,
    };
  });
}

export interface DashboardTotals {
  revenueTarget: number | null;
  profitTarget: number | null;
  actualRevenue: number;
  actualProfit: PartialSum;
  forecastRevenue: number;
  forecastProfit: PartialSum;
  /** Months of the period not yet elapsed (for the required pace). */
  remainingMonths: number;
}

function nullableTotal(values: (number | null)[]): number | null {
  const known = values.filter((value): value is number => value != null);
  return known.length === 0 ? null : known.reduce((sum, value) => sum + value, 0);
}

export function totalsFor(months: DashboardMonth[]): DashboardTotals {
  return {
    revenueTarget: nullableTotal(months.map((month) => month.revenueTarget)),
    profitTarget: nullableTotal(months.map((month) => month.profitTarget)),
    actualRevenue: months.filter((month) => month.elapsed).reduce((sum, month) => sum + month.actualRevenue, 0),
    actualProfit: partialSum(
      months.filter((month) => month.elapsed).map((month) => month.actualProfit),
      [monthsPending(months.filter((month) => month.elapsed), 'actual')],
    ),
    forecastRevenue: months.reduce((sum, month) => sum + month.forecastRevenue, 0),
    forecastProfit: partialSum(months.map((month) => month.forecastProfit), [monthsPending(months, 'forecast')]),
    remainingMonths: months.filter((month) => !month.elapsed || month.isCurrent).length,
  };
}

/** Running total; `null` entries stay null (a gap in the chart), later points keep accumulating the known values. */
export function cumulative(values: (number | null)[]): (number | null)[] {
  let running = 0;
  return values.map((value) => {
    if (value == null) return null;
    running += value;
    return running;
  });
}

export interface CumulativeSeries {
  target: (number | null)[];
  actual: (number | null)[];
  forecast: (number | null)[];
}

export function revenueSeries(months: DashboardMonth[]): CumulativeSeries {
  return {
    target: months.some((month) => month.revenueTarget != null) ? cumulative(months.map((month) => month.revenueTarget ?? 0)) : [],
    actual: cumulative(months.map((month) => (month.elapsed ? month.actualRevenue : null))),
    forecast: cumulative(months.map((month) => month.forecastRevenue)),
  };
}

export function profitSeries(months: DashboardMonth[]): CumulativeSeries {
  return {
    target: months.some((month) => month.profitTarget != null) ? cumulative(months.map((month) => month.profitTarget ?? 0)) : [],
    // An unknown month (pending FIFO cost) is a gap, never a fake 0.
    actual: cumulative(months.map((month) => (month.elapsed ? month.actualProfit : null))),
    forecast: cumulative(months.map((month) => month.forecastProfit)),
  };
}

/** Progress in percent of a target; null without a positive target. */
export function progressPercent(value: number | null, target: number | null): number | null {
  if (value == null || target == null || target <= 0) return null;
  return (value / target) * 100;
}

export function ratioPercent(numerator: number | null, denominator: number | null): number | null {
  if (numerator == null || denominator == null || denominator === 0) return null;
  return (numerator / denominator) * 100;
}

/**
 * Sum of a line field over the given months on one basis. `withCost`: the
 * field depends on the cost of goods sold (cost, margin, EBITDA, tax, profit),
 * so a line with an incomplete cost marks the sum.
 */
export function sumLine(
  months: DashboardMonth[],
  basis: 'actual' | 'forecast',
  pick: (line: IncomeStatementLine) => number | null,
  withCost = false,
): PartialSum {
  return partialSum(
    months.map((month) => pick(month[basis])),
    withCost ? [monthsPending(months, basis)] : [],
  );
}

/**
 * Margin % of the months on one basis: margin / revenue of the amounts shown
 * (decision 2026-10-29 pt 8). `partial`: some ZK have only a known part of
 * their cost, so the percent is overstated („*”).
 */
export function marginPercentFor(months: DashboardMonth[], basis: 'actual' | 'forecast'): { value: number | null; partial: boolean } {
  let revenue = 0;
  let margin = 0;
  let partial: boolean;
  months.forEach((month) => {
    const line = month[basis];
    revenue += line.revenue;
    margin += line.margin ?? 0;
  });
  partial = monthsPending(months, basis);
  return { value: revenue !== 0 ? (margin / revenue) * 100 : null, partial };
}

export function fixedCostsByCategory(months: DashboardMonth[], basis: 'actual' | 'forecast'): Record<string, number> {
  const result: Record<string, number> = {};
  months.forEach((month) => {
    Object.entries(month[basis].operatingFixedCosts.byCategory).forEach(([category, amount]) => {
      result[category] = (result[category] ?? 0) + amount;
    });
  });
  return result;
}

export interface Slice {
  key: string;
  label: string;
  value: number;
}

/** Drops empty/negative slices and folds the tail beyond `max` into one "other" slice. */
export function foldDashboardSlices(slices: Slice[], max: number, otherLabel: string): Slice[] {
  const positive = slices.filter((slice) => slice.value > 0).sort((a, b) => b.value - a.value);
  if (positive.length <= max) return positive;
  const head = positive.slice(0, max - 1);
  const rest = positive.slice(max - 1).reduce((sum, slice) => sum + slice.value, 0);
  return [...head, { key: 'other', label: otherLabel, value: rest }];
}

/** Sum of plan values; null when no month of the sum has that plan. */
function planSum(months: DashboardMonth[], pick: (plan: BudgetPlan) => number | null): number | null {
  return nullableTotal(months.map((month) => (month.plan ? pick(month.plan) : null)));
}

export interface OutlookRow {
  key: 'revenue' | 'margin' | 'fixedCosts' | 'ebitda' | 'profit';
  /** Plan of the whole year; null without a plan. */
  plan: number | null;
  /** Realization of the elapsed months. */
  actual: PartialSum;
  /** Expected close of the year (EAC): realization + forecast of the rest, scenario applied. */
  close: PartialSum;
  /** close − plan; null without a plan. */
  deviation: number | null;
}

/**
 * „Przewidywane zamknięcie roku” (EAC, decision 2026-10-29-grupy-kosztow-i-budzet):
 * per measure the plan of the year, the realization so far and the expected
 * close = realization + the forecast of the rest of the year (the forecast
 * basis of the same income-statement lines; the scenario moves only the
 * planned part, as everywhere on the Pulpit). Fixed costs = operating fixed
 * costs (the EBITDA line); the profit plan is the entered net profit target.
 * `months`: the 12 months of the year.
 */
export function yearEndOutlook(months: DashboardMonth[]): OutlookRow[] {
  const elapsed = months.filter((month) => month.elapsed);
  const actualPending = monthsPending(elapsed, 'actual');
  const forecastPending = monthsPending(months, 'forecast');
  const row = (key: OutlookRow['key'], plan: number | null, actual: PartialSum, close: PartialSum): OutlookRow => ({
    key, plan, actual, close, deviation: plan == null || close.value == null ? null : close.value - plan,
  });
  const fixedActual = elapsed.reduce((sum, month) => sum + month.actual.operatingFixedCosts.total, 0);
  const fixedClose = months.reduce((sum, month) => sum + month.forecast.operatingFixedCosts.total, 0);
  const marginActual = partialSum(elapsed.map((month) => month.actual.margin), [actualPending]);
  const marginClose = partialSum(months.map((month) => month.forecastMargin), [forecastPending]);
  const ebitdaOf = (margin: PartialSum, fixed: number): PartialSum => ({
    value: margin.value == null ? null : margin.value - fixed,
    incomplete: margin.incomplete,
  });
  return [
    row('revenue', planSum(months, (plan) => (plan.revenue > 0 ? plan.revenue : null)),
      { value: elapsed.reduce((sum, month) => sum + month.actualRevenue, 0), incomplete: false },
      { value: months.reduce((sum, month) => sum + month.forecastRevenue, 0), incomplete: false }),
    row('margin', planSum(months, (plan) => plan.margin), marginActual, marginClose),
    row('fixedCosts', planSum(months, (plan) => plan.fixedCosts), { value: fixedActual, incomplete: false }, { value: fixedClose, incomplete: false }),
    row('ebitda', planSum(months, (plan) => plan.ebitda), ebitdaOf(marginActual, fixedActual), ebitdaOf(marginClose, fixedClose)),
    row('profit', planSum(months, (plan) => plan.netProfit),
      partialSum(elapsed.map((month) => month.actualProfit), [actualPending]),
      partialSum(months.map((month) => month.forecastProfit), [forecastPending])),
  ];
}

export interface GroupVarianceRow {
  /** Group id, or UNGROUPED_COST_GROUP for „Bez grupy”. */
  key: string;
  group: CompanyCostGroup | null;
  /** Plan of the period; null when no month of the period plans it. */
  plan: number | null;
  /** Operating fixed costs of the elapsed months of the period (income statement, actual). */
  actual: number;
  /** Elapsed + scheduled costs of the rest of the period (income statement, forecast). */
  forecast: number;
  /** forecast − plan; null without a plan. */
  difference: number | null;
}

/**
 * „Koszty stałe w grupach” of „Koszty i rentowność”: plan, actual and
 * forecast per cost group in the months of the page's period. Actual and
 * forecast are the `operatingFixedCosts.byGroup` of the same income-statement
 * lines (the rows add up to the operating fixed costs), the plan is the
 * budget. Groups in their order (active first); an archived or unknown group
 * appears only with money or a plan; „Bez grupy” last.
 */
export function groupVariance(periodMonths: DashboardMonth[], groups: CompanyCostGroup[]): GroupVarianceRow[] {
  const elapsed = periodMonths.filter((month) => month.elapsed);
  const sumBy = (list: DashboardMonth[], basis: 'actual' | 'forecast', key: string) => list
    .reduce((sum, month) => sum + (month[basis].operatingFixedCosts.byGroup?.[key] ?? 0), 0);
  const planOf = (key: string) => nullableTotal(periodMonths.map((month) => {
    if (!month.plan) return null;
    if (key === UNGROUPED_COST_GROUP) return month.plan.ungrouped;
    return month.plan.groups.find((group) => group.groupId === key)?.amount ?? null;
  }));
  const byId = new Map(groups.map((group) => [group.id, group]));
  const keys = new Set<string>();
  periodMonths.forEach((month) => {
    Object.keys(month.forecast.operatingFixedCosts.byGroup ?? {}).forEach((key) => keys.add(key));
    Object.keys(month.actual.operatingFixedCosts.byGroup ?? {}).forEach((key) => keys.add(key));
    month.plan?.groups.forEach((group) => keys.add(group.groupId));
  });
  groups.filter((group) => group.isActive).forEach((group) => keys.add(group.id));
  keys.delete(UNGROUPED_COST_GROUP);
  const ordered = Array.from(keys).sort((a, b) => {
    const left = byId.get(a);
    const right = byId.get(b);
    if (!left || !right) return left ? -1 : right ? 1 : a.localeCompare(b);
    return Number(right.isActive) - Number(left.isActive) || left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, 'pl');
  });
  const row = (key: string): GroupVarianceRow => {
    const plan = planOf(key);
    const forecast = sumBy(periodMonths, 'forecast', key);
    return { key, group: byId.get(key) ?? null, plan, actual: sumBy(elapsed, 'actual', key), forecast, difference: plan == null ? null : forecast - plan };
  };
  return [...ordered.map(row), row(UNGROUPED_COST_GROUP)];
}

export interface FixedCostsToDate {
  /** Fixed-cost plan of the elapsed months of the period (current month included); null without a plan. */
  plan: number | null;
  /** Operating fixed costs of the same months (income statement, actual). */
  actual: number;
  /** Months counted. */
  months: number;
}

/**
 * Plan against realization for the months that already happened — the fair
 * comparison next to the period plan of „Koszty stałe w grupach” (the rest of
 * the period has no realization yet).
 */
export function fixedCostsToDate(periodMonths: DashboardMonth[]): FixedCostsToDate {
  const elapsed = periodMonths.filter((month) => month.elapsed);
  // A difference against a plan that covers only some of the months would
  // compare unequal spans, so the plan is unknown unless every month has one.
  const plans = elapsed.map((month) => (month.plan ? month.plan.fixedCosts : null));
  return {
    plan: plans.some((value) => value == null) ? null : nullableTotal(plans),
    actual: elapsed.reduce((sum, month) => sum + month.actual.operatingFixedCosts.total, 0),
    months: elapsed.length,
  };
}
